import { useEffect, useState } from "react";
import { Info, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  FEEDBACK_CATEGORIES,
  submitUserFeedback,
  type FeedbackCategory,
  type FeedbackContext,
} from "@/lib/userFeedback";
import { cn } from "@/lib/utils";

type FeedbackSheetProps = {
  open: boolean;
  onClose: () => void;
  defaultCategory?: FeedbackCategory;
  context?: FeedbackContext;
};

export function FeedbackSheet({
  open,
  onClose,
  defaultCategory = "other",
  context,
}: FeedbackSheetProps) {
  const { t } = useTranslation("consumer");
  const { user, isGuest } = useAuth();
  const [category, setCategory] = useState<FeedbackCategory>(defaultCategory);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCategory(defaultCategory);
    setMessage("");
    setError(null);
  }, [open, defaultCategory]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleSubmit = async () => {
    if (isGuest || !user?.id) return;

    const trimmed = message.trim();
    if (!trimmed) {
      setError(t("feedback.emptyMessage"));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await submitUserFeedback(user.id, category, trimmed, context);
      toast.success(t("feedback.toastSuccess"));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("feedback.submitError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const needsAuth = isGuest || !user?.id;

  return (
    <>
      <div
        className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[90] flex items-center justify-center px-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-modal-title"
          data-feedback-modal=""
          className="w-full max-w-sm rounded-3xl border border-border bg-surface-elevated p-5 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-4 flex items-start justify-between gap-3">
            <h2 id="feedback-modal-title" className="font-headline text-lg font-bold text-foreground">
              {t("feedback.title")}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("feedback.close")}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-border bg-surface"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>

          {needsAuth ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">{t("feedback.authTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("feedback.authHint")}</p>
            </div>
          ) : (
            <div className="space-y-5">
              <p className="text-sm leading-relaxed text-muted-foreground">{t("feedback.intro")}</p>

              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("feedback.categoryLabel")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {FEEDBACK_CATEGORIES.map((id) => {
                    const active = category === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setCategory(id)}
                        className={cn(
                          "inline-flex shrink-0 items-center rounded-full px-3 py-1.5 text-xs font-semibold transition-all",
                          active
                            ? "bg-gradient-pulse text-pulse-foreground shadow-pulse"
                            : "border border-border bg-surface text-muted-foreground hover:border-pulse/40 hover:text-foreground",
                        )}
                      >
                        {t(`feedback.categories.${id}`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <span className="mb-2.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("feedback.messageLabel")}
                </span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  placeholder={t("feedback.messagePlaceholder")}
                  className="w-full resize-none rounded-2xl border border-border bg-surface px-4 py-3 text-sm leading-relaxed outline-none transition-colors focus:border-pulse"
                />
              </label>

              {error && (
                <p className="rounded-2xl border border-pulse/30 bg-pulse/10 px-4 py-3 text-sm text-pulse">
                  {error}
                </p>
              )}

              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  disabled={submitting || message.trim().length === 0}
                  onClick={() => void handleSubmit()}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-pulse px-6 py-3 text-sm font-bold text-pulse-foreground shadow-pulse transition-transform disabled:opacity-60 active:scale-95"
                >
                  {submitting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    t("feedback.submit")
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

type ReportWrongDataButtonProps = {
  defaultCategory: "wrong_event_info" | "wrong_venue_info";
  context: FeedbackContext;
  className?: string;
};

export const reportWrongDataIconClass =
  "grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/10 bg-black/40 text-pulse backdrop-blur-sm transition-colors hover:bg-black/55 active:scale-95";

export function ReportWrongDataButton({
  defaultCategory,
  context,
  className,
}: ReportWrongDataButtonProps) {
  const { t } = useTranslation("consumer");
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("feedback.reportWrongDataAria")}
        className={className ?? reportWrongDataIconClass}
      >
        <Info className="h-5 w-5" aria-hidden="true" />
      </button>
      <FeedbackSheet
        open={open}
        onClose={() => setOpen(false)}
        defaultCategory={defaultCategory}
        context={context}
      />
    </>
  );
}
